import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSEO } from '../../hooks/useSEO';
import { fetchJourneyPath } from '../../lib/contentClient';
import { resolveField } from '../../lib/contentText';
import { ContentLanguageToggle, useContentLanguage } from '../../contexts/ContentLanguageContext';
import LessonMarkdown from '../../components/LessonMarkdown';
import LessonCopy, { KeyTerms } from '../../components/LessonCopy';
import { lessonProgressService } from '../../lib/lessonProgressService';
import { useUserType } from '../../hooks/useUserType';
import { usePremiumAccess } from '../../hooks/usePremiumAccess';
import type { JourneyPath, Module } from '../../types/journey';
import { 
  AlertCircle, 
  BookOpen, 
  Clock, 
  Star, 
  Users, 
  CheckCircle, 
  Lock, 
  PlayCircle, 
  FileText, 
  HelpCircle, 
  ChevronRight, 
  ChevronDown, 
  ChevronUp,
  AlignCenterVertical as Certificate, 
  GraduationCap, 
  Award, 
  Lightbulb, 
  Target,
  ArrowLeft,
  Crown
} from 'lucide-react';
import { ComingSoonBadge } from '../../components/ComingSoonBadge';
import PremiumUpgradeModal from '../../components/PremiumUpgradeModal';
import { useQuery } from '@tanstack/react-query';

const CourseDetail = () => {
  const { language } = useLanguage();
  const { lang } = useContentLanguage();
  const navigate = useNavigate();
  const { slug } = useParams();
  const { userType, loading: userTypeLoading } = useUserType();
  const { hasPremiumAccess, loading: premiumLoading } = usePremiumAccess();
  const [activeTab, setActiveTab] = useState<'overview' | 'curriculum' | 'reviews'>('curriculum');
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const [progress, setProgress] = useState<Record<string, boolean>>({});
  const [overallProgress, setOverallProgress] = useState(0);
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  // Filter modules based on user type and premium access
  const getFilteredModules = (modules: Module[]): Module[] => {
    if (!modules) return [];
    
    return modules.filter(module => {
      // Check target audience
      if (module.targetAudience && module.targetAudience !== 'all' && module.targetAudience !== userType) {
        return false;
      }
      
      // Check premium access - show premium modules to all users but mark them as premium
      // This allows users to see what they're missing and encourages upgrades
      return true;
    });
  };

  // Check if a module requires premium access
  const isModulePremium = (module: Module): boolean => {
    return module.isPremium || false;
  };

  // Check if the entire course is premium (all modules are premium)
  const isCoursePremium = (): boolean => {
    if (!sortedModules || sortedModules.length === 0) return false;
    return sortedModules.every(module => module.isPremium);
  };

  // Handle premium upgrade
  const handlePremiumUpgrade = () => {
    setShowPremiumModal(false);
    navigate('/subscription');
  };

  // Update the query to explicitly request all module fields and lessons for progress calculation
  const { data: path, isLoading, error } = useQuery({
    queryKey: ['journeyPath', slug, userType],
    queryFn: async () => {
      // Wait for user type to be loaded
      if (userTypeLoading) {
        return null;
      }
      
      console.log('Fetching course detail for user type:', userType);
      const data = await fetchJourneyPath(slug || '', userType || undefined);
      
      if (!data) {
        console.error('No data returned from fetchJourneyPath');
        return null;
      }
      
      // Ensure modules and lessons are sorted right after fetching for reliable navigation
      if (data) {
        console.log('Raw data from Sanity:', data);
        console.log('Modules field:', data.modules);
        console.log('Modules type:', typeof data.modules);
        console.log('Is modules array?', Array.isArray(data.modules));
        
        // Ensure modules is an array. Do not invent lessons here.
        // Launch scaffolding lives in courseLaunch so empty courses stay gated.
        if (!Array.isArray(data.modules)) {
          console.warn('Modules is not an array, setting to empty array');
          data.modules = [];
        }
        
        if (data.modules && data.modules.length > 0) {
        data.modules.sort((a, b) => (a.order || 0) - (b.order || 0));
        data.modules.forEach(module => {
            if (module.lessons && Array.isArray(module.lessons)) {
            module.lessons.sort((a, b) => (a.order || 0) - (b.order || 0));
          }
        });
        }
        
        console.log('Filtered course data:', {
          title: data.title,
          totalModules: data.modules.length,
          targetAudience: data.targetAudience,
          modules: data.modules.map(m => ({
            title: m.title,
            lessonsCount: m.lessons?.length || 0,
            lessons: m.lessons?.map(l => ({ title: l.title, slug: l.slug })) || []
          }))
        });
        
        // Additional debugging for modules and lessons
        console.log('Raw modules data:', data.modules);
        data.modules.forEach((module, index) => {
          console.log(`Module ${index + 1}:`, {
            title: module.title,
            order: module.order,
            lessonsCount: module.lessons?.length || 0,
            lessons: module.lessons
          });
        });
      }
      return data;
    },
    enabled: !!slug && !userTypeLoading,
  });

  const textOf = (item?: { title?: string; titleMy?: string; titleEn?: string } | null) =>
    resolveField(path?.primaryLanguage === 'en' ? 'en' : lang, item?.titleMy, item?.titleEn, item?.title).text;

  // SEO optimization
  useSEO({
    title: path?.title ? `${path.title} - Course Details` : 'Course Details',
    description: path?.introduction ? 
      (Array.isArray(path.introduction) ? 
        path.introduction[0]?.children?.[0]?.text || 'Learn essential skills for life in America' :
        'Learn essential skills for life in America') :
      'Comprehensive course designed for immigrants to succeed in America',
    keywords: ['immigration course', 'financial education', 'life skills', 'U.S. immigrants'],
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Dashboard', url: '/dashboard' },
      { name: 'Journey Hub', url: '/journey' },
      { name: path?.title || 'Course', url: `/courses/${slug}` }
    ]
  });

  useEffect(() => {
    if (path && slug) {
      // Full sync with Supabase for this journey
      lessonProgressService.fullSyncWithJourney(slug, new Set()).then(() => {
        // Update progress with synced data
      loadProgress(path);
      }).catch(error => {
        console.error('Sync failed, using local data:', error);
        loadProgress(path);
      });
    }
  }, [path, slug]);

  const loadProgress = async (journeyPath: JourneyPath) => {
    try {
      const lessonIds = (journeyPath.modules || [])
        .flatMap(module => (module.lessons || []))
        .map(lesson => lesson._id)
        .filter(Boolean);

      if (lessonIds.length === 0) {
          setOverallProgress(0);
          return;
      }

      // Load completed lessons using hybrid service
      const completedLessons = await lessonProgressService.getCompletedLessons();
      
      const progressMap: Record<string, boolean> = {};
      completedLessons.forEach((lessonId: string) => {
        progressMap[lessonId] = true;
      });

      setProgress(progressMap);

      const totalLessons = lessonIds.length;
      const completedCount = completedLessons.size;
      setOverallProgress(totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0);
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };


  // Get module completion status
  const getModuleProgress = (module: Module) => {
    if (!module.lessons || module.lessons.length === 0) return 0;
    
    const completedLessons = module.lessons.filter(lesson => progress[lesson._id]).length;
    return Math.round((completedLessons / module.lessons.length) * 100);
  };

  // Get module status
  const getModuleStatus = (module: Module) => {
    const moduleProgress = getModuleProgress(module);
    
    if (moduleProgress === 100) return 'completed';
    if (moduleProgress > 0) return 'in-progress';
    
    // Check if previous module is completed or in progress
    const moduleIndex = path?.modules.findIndex(m => m._id === module._id) || 0;
    if (moduleIndex === 0) return 'available'; // First module is always available
    
    const prevModule = path?.modules[moduleIndex - 1];
    if (!prevModule) return 'available';
    
    const prevModuleProgress = getModuleProgress(prevModule);
    return prevModuleProgress > 0 ? 'available' : 'locked';
  };

  // Get icon for module
  const getModuleIcon = (moduleIndex: number) => {
    const icons = [BookOpen, Target, Award, Lightbulb, Certificate, Users, FileText, PlayCircle];
    return icons[moduleIndex % icons.length];
  };

  if (isLoading || userTypeLoading || premiumLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-600">Loading personalized content...</p>
        </div>
      </div>
    );
  }

  if (error || !path) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          {error instanceof Error ? error.message : 'No journey paths found'}
        </h2>
        <p className="text-gray-600 mb-4">Please try again later</p>
        <button 
          onClick={() => navigate('/guides')}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Back to Guides
        </button>
      </div>
    );
  }

  if (path.launchReady !== true) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <ComingSoonBadge className="mb-4" />
        <h1 className="font-myanmar text-3xl font-bold text-gray-900 mt-4 mb-3 break-words">{textOf(path)}</h1>
        <p className="text-gray-600 mb-2">
          {path.shortDescription || 'This course is listed, but the lessons are not ready to start yet.'}
        </p>
        <p className="text-gray-500 mb-8">
          You can begin with New to America, Budgeting for a Fresh Start, or Health Insurance Basics.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate('/courses/new-to-america')}
            className="bg-primary-600 text-white px-5 py-3 rounded-xl font-semibold hover:bg-primary-700"
          >
            Start New to America
          </button>
          <button
            onClick={() => navigate('/guides')}
            className="bg-white text-gray-800 border border-gray-300 px-5 py-3 rounded-xl font-semibold hover:bg-gray-50"
          >
            Back to Guides
          </button>
        </div>
      </div>
    );
  }

  // Sort and filter modules by order and access before rendering
  const sortedModules = Array.isArray(path.modules) 
    ? getFilteredModules([...path.modules].sort((a, b) => (a.order || 0) - (b.order || 0)))
    : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Modern Companion-Style Header */}
      <div className="relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 via-transparent to-green-400/20"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-10 right-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-10 left-10 w-16 h-16 bg-yellow-300/20 rounded-full blur-lg"></div>
        
        {/* Content */}
        <div className="relative z-10">
          {/* Back Button */}
          <div className="px-4 py-4">
          <button
              onClick={() => navigate('/guides')}
              className="flex items-center text-white/90 hover:text-white transition-all duration-200 group"
            >
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-2 mr-3 group-hover:bg-white/30 transition-colors">
                <ArrowLeft className="h-4 w-4" />
              </div>
              <span className="font-medium">Back to your journey</span>
          </button>
          </div>
          
          {/* Hero Section */}
          <div className="px-4 pb-8">
            <div className="max-w-4xl mx-auto">
              {/* Progress Celebration */}
              {overallProgress > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white/90 text-sm font-medium">🎉 You're doing amazing!</span>
                    <span className="text-white font-bold text-lg">{overallProgress}%</span>
            </div>
                  <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-yellow-300 via-green-400 to-emerald-500 h-3 rounded-full transition-all duration-700 shadow-lg"
                      style={{ width: `${overallProgress}%` }}
                    />
                  </div>
                </div>
              )}
              
              {/* Course Title with Emoji */}
              {path.primaryLanguage === 'en' ? (
                <p className="mb-3 text-sm text-white/80">English is the main text. Burmese appears under each block.</p>
              ) : (
                <div className="mb-4">
                  <ContentLanguageToggle />
                </div>
              )}
              <div className="flex items-center flex-wrap gap-3 mb-2">
                <h1 className="font-myanmar text-3xl md:text-4xl font-bold leading-tight text-white break-words">
                  {textOf(path)}
                </h1>
                {isCoursePremium() && !hasPremiumAccess && (
                  <span className="px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full text-sm font-bold flex items-center">
                    <Crown className="w-4 h-4 mr-1" />
                    Premium Journey
                  </span>
                )}
              </div>
              
              {path.primaryLanguage === 'en' && path.titleMy && (
                <p className="font-myanmar mb-4 text-lg text-white/75 break-words" lang="my">{path.titleMy}</p>
              )}
              
              {/* Course Description */}
              <div className="lesson-on-dark mb-6 text-lg leading-relaxed">
                {path.primaryLanguage === 'en' ? (
                  <LessonCopy primary="en" lang={lang} my={path.bodyMy} en={path.bodyEn} legacy={path.introduction || path.shortDescription} />
                ) : (
                  <LessonMarkdown value={resolveField(lang, path.bodyMy, path.bodyEn, path.introduction || path.shortDescription).text} />
                )}
              </div>
              {(path.glossary || []).length > 0 && (
                <div className="mb-6 text-gray-900">
                  <KeyTerms glossary={path.glossary} />
                </div>
              )}
              
              {/* Fun Meta Info */}
              <div className="flex flex-wrap gap-3 mb-6">
                <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-3 rounded-2xl text-white">
                  <Clock className="h-5 w-5 mr-2" />
                  <span className="font-medium">{path.duration}</span>
                </div>
                <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-3 rounded-2xl text-white">
                  <GraduationCap className="h-5 w-5 mr-2" />
                  <span className="font-medium capitalize">{path.level}</span>
              </div>
              {path.rating && (
                  <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-3 rounded-2xl text-white">
                    <Star className="h-5 w-5 text-yellow-300 mr-2" />
                    <span className="font-medium">{path.rating}</span>
                </div>
              )}
            </div>

              {/* Encouragement Card */}
              <div className="bg-white/15 backdrop-blur-md rounded-3xl p-6 border border-white/20 shadow-xl">
                <div className="flex items-start">
                  <div className="bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl p-3 mr-4 flex-shrink-0 shadow-lg">
                    <CheckCircle className="h-6 w-6 text-white" />
                </div>
                  <div>
                    <h3 className="font-bold text-white mb-2 text-lg">You've got this! 💪</h3>
                    <p className="text-white/90 leading-relaxed">
                      We're here to guide you every step of the way. This journey is designed just for you - take it at your own pace and celebrate every small win!
                </p>
              </div>
          </div>
        </div>
      </div>
          </div>
        </div>
      </div>

      {/* Modern Tab Navigation */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-lg">
        <div className="px-4 py-2">
          <div className="flex bg-gray-100 rounded-2xl p-1">
            {(['overview', 'curriculum', 'reviews'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-3 px-4 text-sm font-medium transition-all duration-300 rounded-xl ${
                  activeTab === tab
                    ? 'bg-white text-purple-600 shadow-md transform scale-105'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-white/50'
                }`}
              >
                <div className="flex items-center justify-center">
                  {tab === 'overview' && <span className="mr-2">📖</span>}
                  {tab === 'curriculum' && <span className="mr-2">🎯</span>}
                  {tab === 'reviews' && <span className="mr-2">⭐</span>}
                  <span>
                    {tab === 'overview' ? 'Get Started' : 
                     tab === 'curriculum' ? 'Your Path' : 'Community'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content - Mobile First */}
      <div className="px-4 py-6 md:px-8 md:py-8 max-w-7xl mx-auto">
        <div className="md:grid md:grid-cols-3 md:gap-8">
          {/* Main Column */}
          <div className="md:col-span-2 space-y-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Welcome Card */}
                <div className="bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-3xl p-8 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-2xl font-bold">Welcome to your journey! 🌟</h2>
                      {isCoursePremium() && !hasPremiumAccess && (
                        <span className="px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full text-sm font-bold flex items-center">
                          <Crown className="w-4 h-4 mr-1" />
                          Premium
                        </span>
                      )}
                    </div>
                    <p className="text-white/90 mb-6 text-lg leading-relaxed">
                      {isCoursePremium() && !hasPremiumAccess ? (
                        <>
                          This is our premium journey with advanced content and personalized guidance. 
                          <span className="font-semibold"> Unlock your full potential</span> with exclusive modules designed to accelerate your success in America!
                        </>
                      ) : (
                        "You're about to embark on an amazing adventure that will help you build a strong foundation in America. We're so excited to be part of your story!"
                      )}
                    </p>
                    
                    {/* Progress Indicator */}
                    {overallProgress > 0 && (
                      <div className="mb-6 p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white/90 text-sm font-medium">Your Progress</span>
                          <span className="text-white font-bold">{overallProgress}%</span>
                        </div>
                        <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-yellow-300 via-green-400 to-emerald-500 h-2 rounded-full transition-all duration-700 shadow-sm"
                            style={{ width: `${overallProgress}%` }}
                          />
                        </div>
                        <p className="text-white/80 text-xs mt-2">
                          {Math.round((overallProgress / 100) * sortedModules.flatMap(m => m.lessons || []).length)} of {sortedModules.flatMap(m => m.lessons || []).length} lessons completed
                        </p>
                      </div>
                    )}
                    
                    <button
                      onClick={() => {
                        // If course is premium and user doesn't have access, show upgrade modal
                        if (isCoursePremium() && !hasPremiumAccess) {
                          setShowPremiumModal(true);
                          return;
                        }
                        
                        // Check if first module has action plan, otherwise go to curriculum
                        const firstModule = sortedModules[0];
                        const firstLesson = firstModule?.lessons?.[0];
                        const hasActionPlan = firstLesson?.actionableTasks && firstLesson.actionableTasks.length > 0;
                        
                        if (hasActionPlan && firstLesson) {
                          navigate(`/courses/${path.slug}/lessons/${firstLesson.slug}`);
                        } else {
                          setActiveTab('curriculum');
                        }
                      }}
                      className={`px-6 py-3 rounded-2xl font-bold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 ${
                        isCoursePremium() && !hasPremiumAccess
                          ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white hover:from-yellow-500 hover:to-orange-600'
                          : 'bg-white text-purple-600 hover:bg-white/90'
                      }`}
                    >
                      {isCoursePremium() && !hasPremiumAccess ? (
                        <div className="flex items-center space-x-2">
                          <Crown className="w-5 h-5" />
                          <span>Unlock Premium Journey</span>
                        </div>
                      ) : (
                        sortedModules[0]?.lessons?.[0]?.actionableTasks && sortedModules[0].lessons[0].actionableTasks.length > 0 ? 
                          'Start Your Action Plan' : 
                          'Begin Learning Journey'
                      )}
                    </button>
                  </div>
                </div>

                {/* What You'll Discover */}
                {path.journeyOutcomes && (
                  <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                      <span className="mr-3">🎯</span>
                      What You'll Discover
                    </h2>
                    <div className="prose max-w-none text-gray-600 text-lg leading-relaxed">
                      <LessonMarkdown value={Array.isArray(path.journeyOutcomes) ? path.journeyOutcomes : [path.journeyOutcomes]} />
                    </div>
                  </div>
                )}
                
                {/* How This Transforms Your Life */}
                {path.howItHelps && (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-3xl p-8 border border-blue-200">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                      <span className="mr-3">✨</span>
                      How This Transforms Your Life
                    </h2>
                    <div className="prose max-w-none text-gray-700 text-lg leading-relaxed">
                      <LessonMarkdown value={Array.isArray(path.howItHelps) ? path.howItHelps : [path.howItHelps]} />
                    </div>
                  </div>
                )}
                
                {/* Your Learning Journey */}
                {sortedModules.length > 0 && (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-3xl p-8 border border-blue-200">
                    <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                      <span className="mr-3">Your Learning Journey</span>
                    </h3>
                    <div className="grid gap-6">
                      {sortedModules.slice(0, 3).map((module, index) => {
                        const ModuleIcon = getModuleIcon(index);
                        const moduleProgress = getModuleProgress(module);
                        const moduleStatus = getModuleStatus(module);
                        const isPremium = isModulePremium(module);
                        
                        // Get actionable tasks from the first lesson of this module
                        const firstLesson = module.lessons?.[0];
                        const hasActionPlan = firstLesson?.actionableTasks && firstLesson.actionableTasks.length > 0;
                        const actionPlanCount = firstLesson?.actionableTasks?.length || 0;
                        
                        return (
                          <div key={module._id} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-start">
                                <div className={`rounded-full p-3 mr-4 flex-shrink-0 ${
                                  moduleStatus === 'completed' ? 'bg-green-500' : 
                                  moduleStatus === 'in-progress' ? 'bg-blue-500' :
                                  moduleStatus === 'available' ? 'bg-purple-500' : 'bg-gray-400'
                                }`}>
                                  {moduleStatus === 'completed' ? (
                                    <CheckCircle className="h-6 w-6 text-white" />
                                  ) : (
                                    <ModuleIcon className="h-6 w-6 text-white" />
                                  )}
                    </div>
                                <div className="flex-grow">
                                  <div className="flex items-center mb-2">
                                    <h4 className="font-myanmar text-xl font-bold text-gray-800 break-words">{textOf(module)}</h4>
                                    {isPremium && !hasPremiumAccess && (
                                      <span className="ml-2 px-2 py-1 bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 rounded-full text-xs font-bold flex items-center">
                                        <Crown className="h-3 w-3 mr-1" />
                                        Premium
                                      </span>
                                    )}
                  </div>
                                  {module.description && (
                                    <p className="text-gray-600 text-sm mb-3">
                                      {typeof module.description === 'string' ? 
                                        module.description : 
                                        'Learn essential concepts and practical skills'
                                      }
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm text-gray-500 mb-1">
                                  {module.lessons?.length || 0} lessons
                                </div>
                                {moduleStatus !== 'locked' && moduleProgress > 0 && (
                                  <div className="w-20 bg-gray-200 rounded-full h-2 overflow-hidden">
                                    <div 
                                      className={`h-2 rounded-full transition-all duration-500 ${
                                        moduleStatus === 'completed' ? 'bg-green-400' : 'bg-blue-400'
                                      }`}
                                      style={{ width: `${moduleProgress}%` }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Action Plan Preview */}
                            {hasActionPlan && (
                              <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                                <div className="flex items-center mb-2">
                                  <Target className="h-5 w-5 text-green-600 mr-2" />
                                  <span className="font-semibold text-green-800">Your Action Plan</span>
                                  <span className="ml-2 text-sm text-green-600">({actionPlanCount} tasks)</span>
                                </div>
                                <div className="text-sm text-green-700">
                                  {firstLesson.actionableTasks?.slice(0, 2).map((task) => (
                                    <div key={task._key} className="flex items-center mb-1">
                                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></div>
                                      <span>
                                        Complete your action plan
                                      </span>
                                    </div>
                                  ))}
                                  {actionPlanCount > 2 && (
                                    <div className="text-xs text-green-600 mt-1">
                                      +{actionPlanCount - 2} more tasks
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Action Buttons */}
                            <div className="flex gap-3">
                              {hasActionPlan && moduleStatus !== 'locked' && (
                                <button
                                  onClick={() => {
                                    if (isPremium && !hasPremiumAccess) {
                                      setShowPremiumModal(true);
                                      return;
                                    }
                                    if (firstLesson) {
                                      navigate(`/courses/${path.slug}/lessons/${firstLesson.slug}`);
                                    }
                                  }}
                                  className={`flex-1 px-4 py-3 rounded-xl font-semibold transition-all duration-200 shadow-md hover:shadow-lg ${
                                    isPremium && !hasPremiumAccess
                                      ? 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white hover:from-yellow-600 hover:to-orange-700'
                                      : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700'
                                  }`}
                                >
                                  {isPremium && !hasPremiumAccess ? (
                                    <div className="flex items-center justify-center">
                                      <Crown className="h-4 w-4 mr-2" />
                                      Unlock Premium
                                    </div>
                                  ) : (
                                    'Start Action Plan'
                                  )}
                                </button>
                              )}
                              <button
                                onClick={() => setActiveTab('curriculum')}
                                className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
                              >
                                View Lessons
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      
                      {sortedModules.length > 3 && (
                        <div className="text-center pt-2">
                          <span className="text-sm text-gray-600 font-medium">
                            +{sortedModules.length - 3} more modules to explore
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Real World Impact */}
                {path.realWorldImpact && (
                  <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                      <span className="mr-3">🌍</span>
                      Real World Impact
                    </h2>
                    <div className="prose max-w-none text-gray-600 text-lg leading-relaxed">
                      <LessonMarkdown value={Array.isArray(path.realWorldImpact) ? path.realWorldImpact : [path.realWorldImpact]} />
                    </div>
                  </div>
                )}

                {/* How to Complete */}
                {path.howToComplete && (
                  <div className="bg-gradient-to-br from-orange-50 to-red-100 rounded-3xl p-8 border border-orange-200">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                      <span className="mr-3">🏆</span>
                      How to Complete This Journey
                    </h2>
                    <div className="prose max-w-none text-gray-700 text-lg leading-relaxed">
                      <LessonMarkdown value={Array.isArray(path.howToComplete) ? path.howToComplete : [path.howToComplete]} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'curriculum' && (
              <div className="space-y-6">
                {/* Journey Progress Header */}
                <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-3xl p-8 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-12 translate-x-12"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-2xl font-bold mb-2">Your Amazing Journey 🗺️</h2>
                        <p className="text-white/90">
                          {sortedModules.length} exciting steps • {path.duration}
                        </p>
                      </div>
                      {overallProgress > 0 && (
                        <div className="text-right">
                          <div className="text-3xl font-bold">{overallProgress}%</div>
                          <div className="text-white/80">Amazing Progress!</div>
                        </div>
                      )}
                  </div>
                    
                    {/* Progress Bar */}
                    {overallProgress > 0 && (
                      <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden">
                        <div 
                          className="bg-gradient-to-r from-yellow-300 via-green-400 to-emerald-500 h-3 rounded-full transition-all duration-700 shadow-lg"
                          style={{ width: `${overallProgress}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Smart Continue Learning Button */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-100 rounded-3xl p-6 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">Ready to Continue?</h3>
                      <p className="text-gray-600 text-sm">
                        {(() => {
                          // Find the next uncompleted lesson
                          const nextLesson = sortedModules
                            .flatMap(module => module.lessons || [])
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .find(lesson => !progress[lesson._id]);
                          
                          if (nextLesson) {
                            const hasActionPlan = nextLesson.actionableTasks && nextLesson.actionableTasks.length > 0;
                            if (hasActionPlan) {
                              return `Start your action plan for "${nextLesson.title}"`;
                            } else {
                              return `Continue with "${nextLesson.title}"`;
                            }
                          } else if (overallProgress === 100) {
                            return "Congratulations! You've completed this journey!";
                          } else {
                            return "Begin your learning journey";
                          }
                        })()}
                      </p>
                    </div>
                    <div className="ml-4">
                      {(() => {
                        // Find the next uncompleted lesson
                        const nextLesson = sortedModules
                          .flatMap(module => module.lessons || [])
                          .sort((a, b) => (a.order || 0) - (b.order || 0))
                          .find(lesson => !progress[lesson._id]);
                        
                        if (nextLesson) {
                          const hasActionPlan = nextLesson.actionableTasks && nextLesson.actionableTasks.length > 0;
                          return (
                            <button
                              onClick={() => navigate(`/courses/${path.slug}/lessons/${nextLesson.slug}`)}
                              className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
                            >
                              {hasActionPlan ? 'Start Action Plan' : 'Continue Learning'}
                            </button>
                          );
                        } else if (overallProgress === 100) {
                          return (
                            <div className="text-center">
                              <div className="text-2xl mb-2">🎉</div>
                              <div className="text-sm text-gray-600 font-medium">All Done!</div>
                            </div>
                          );
                        } else {
                          return (
                            <button
                              onClick={() => {
                                const firstLesson = sortedModules[0]?.lessons?.[0];
                                if (firstLesson) {
                                  navigate(`/courses/${path.slug}/lessons/${firstLesson.slug}`);
                                }
                              }}
                              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg"
                            >
                              Start Learning
                            </button>
                          );
                        }
                      })()}
                    </div>
                  </div>
                </div>

                {/* Completion Celebration */}
                {overallProgress > 0 && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-100 rounded-3xl p-6 border border-green-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-3 mr-4">
                          <CheckCircle className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-green-800 mb-1">Great Progress!</h3>
                          <p className="text-green-700 text-sm">
                            You've completed {Math.round((overallProgress / 100) * sortedModules.flatMap(m => m.lessons || []).length)} out of {sortedModules.flatMap(m => m.lessons || []).length} lessons
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-green-800">{overallProgress}%</div>
                        <div className="text-sm text-green-600">Complete</div>
                      </div>
                    </div>
                    <div className="mt-4 w-full bg-green-200 rounded-full h-3 overflow-hidden">
                      <div 
                        className="bg-gradient-to-r from-green-400 to-emerald-500 h-3 rounded-full transition-all duration-700 shadow-sm"
                        style={{ width: `${overallProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Learning Path */}
                <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">

                  {/* Enhanced Module List with Visual Journey Path - Mobile First */}
                  <div className="relative">
                    {/* Vertical connecting line */}
                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200 z-0"></div>
                    
                    {sortedModules.length > 0 ? sortedModules.map((module, moduleIndex) => {
                      const moduleProgress = getModuleProgress(module);
                      const moduleStatus = getModuleStatus(module);
                      const ModuleIcon = getModuleIcon(moduleIndex);
                      const isPremium = isModulePremium(module);
                      
                      return (
                        <div key={module._id} className="relative z-10">
                          <button
                            onClick={() => {
                              if (isPremium && !hasPremiumAccess) {
                                setShowPremiumModal(true);
                                return;
                              }
                              setExpandedModule(expandedModule === module._id ? null : module._id);
                            }}
                            className={`w-full text-left p-6 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all duration-300 ${
                              expandedModule === module._id ? 'bg-gradient-to-r from-purple-50 to-pink-50' : ''
                            } ${moduleStatus === 'locked' ? 'opacity-60 cursor-not-allowed' : ''} ${
                              isPremium && !hasPremiumAccess ? 'hover:from-yellow-50 hover:to-orange-50' : ''
                            }`}
                            disabled={moduleStatus === 'locked'}
                          >
                            <div className="flex items-start">
                              {/* Module icon with status indicator */}
                              <div className={`flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center z-10 shadow-lg ${
                                moduleStatus === 'completed' ? 'bg-gradient-to-br from-green-400 to-emerald-500' : 
                                moduleStatus === 'in-progress' ? 'bg-gradient-to-br from-blue-400 to-indigo-500' :
                                moduleStatus === 'available' ? 'bg-gradient-to-br from-purple-400 to-pink-500' : 'bg-gray-300'
                              }`}>
                                {moduleStatus === 'completed' ? (
                                  <CheckCircle className="h-8 w-8 text-white" />
                                ) : (
                                  <ModuleIcon className={`h-8 w-8 ${
                                    moduleStatus === 'in-progress' ? 'text-white' :
                                    moduleStatus === 'available' ? 'text-white' : 'text-gray-500'
                                  }`} />
                                )}
                              </div>
                              
                              <div className="ml-4 flex-grow">
                                <div className="flex items-start justify-between">
                                  <div className="flex-grow">
                                    <div className="flex items-center mb-3">
                                      <span className="text-sm font-bold text-purple-600 bg-purple-100 px-3 py-1 rounded-full mr-3">
                                        Step {moduleIndex + 1}
                                      </span>
                                      {moduleStatus === 'in-progress' && (
                                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold">
                                          🚀 In Progress
                                        </span>
                                      )}
                                      {moduleStatus === 'completed' && (
                                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold">
                                          🎉 Completed!
                                        </span>
                                      )}
                                      {moduleStatus === 'locked' && (
                                        <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-bold flex items-center">
                                          <Lock className="h-3 w-3 mr-1" />
                                          Locked
                                        </span>
                                      )}
                                      {isPremium && !hasPremiumAccess && (
                                        <span className="px-3 py-1 bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 rounded-full text-xs font-bold flex items-center">
                                          <Crown className="h-3 w-3 mr-1" />
                                          Premium
                                        </span>
                                      )}
                                    </div>
                                    
                                    <h3 className={`text-xl font-bold mb-3 ${
                                      moduleStatus === 'locked' ? 'text-gray-400' : 'text-gray-800'
                                    }`}>
                                      {textOf(module)}
                                    </h3>
                                    
                                    {module.description && (
                                      <div className={`text-sm mb-3 ${
                                        moduleStatus === 'locked' ? 'text-gray-400' : 'text-gray-600'
                                      }`}>
                                        {typeof module.description === 'string' ? (
                                          <p>{module.description}</p>
                                        ) : (
                                          <LessonMarkdown value={Array.isArray(module.description) ? module.description : [module.description]} />
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Lesson count */}
                                    <div className="flex items-center text-sm text-gray-500 mb-3">
                                      <span className="mr-2">📚</span>
                                      <span className="font-medium">{module.lessons?.length || 0} amazing lessons</span>
                                    </div>
                                    
                                    {/* Progress bar for the module */}
                                    {moduleStatus !== 'locked' && moduleProgress > 0 && (
                                      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                        <div 
                                          className={`h-3 rounded-full transition-all duration-500 shadow-sm ${
                                            moduleStatus === 'completed' ? 'bg-gradient-to-r from-green-400 to-emerald-500' : 'bg-gradient-to-r from-blue-400 to-indigo-500'
                                          }`}
                                          style={{ width: `${moduleProgress}%` }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Expand/Collapse indicator */}
                                  {moduleStatus !== 'locked' && (
                                    <div className="flex items-center ml-4">
                                      {expandedModule === module._id ? (
                                        <ChevronUp className="h-6 w-6 text-gray-400" />
                                      ) : (
                                        <ChevronDown className="h-6 w-6 text-gray-400" />
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>

                          {expandedModule === module._id && module.lessons && (
                            <div className="px-6 pb-6 bg-gradient-to-br from-purple-50 to-pink-50 border-t border-purple-100">
                              <div className="pt-6">
                                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                  <span className="mr-2">🎯</span>
                                  Your Learning Adventure
                                </h4>
                                <div className="space-y-3">
                              {module.lessons.map((lesson, lessonIndex) => (
                                <button
                                  key={lesson._id}
                                  onClick={() => navigate(`/courses/${path.slug}/lessons/${lesson.slug}`)}
                                      className={`w-full text-left p-5 rounded-2xl transition-all duration-300 flex items-center justify-between ${
                                        moduleStatus === 'locked' 
                                          ? 'cursor-not-allowed opacity-50 bg-gray-100' 
                                          : 'bg-white hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 border border-gray-200 hover:border-purple-200 shadow-md hover:shadow-lg transform hover:scale-105'
                                  }`}
                                  disabled={moduleStatus === 'locked'}
                                >
                                  <div className="flex items-center">
                                    {progress[lesson._id] ? (
                                          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mr-4 shadow-lg">
                                            <CheckCircle className="h-6 w-6 text-white" />
                                      </div>
                                    ) : (
                                          <div className="w-10 h-10 rounded-2xl border-2 border-purple-200 flex items-center justify-center mr-4 bg-white shadow-sm">
                                            <span className="text-sm font-bold text-purple-600">{lessonIndex + 1}</span>
                                      </div>
                                    )}
                                    <div>
                                          <div className={`font-bold text-lg ${moduleStatus === 'locked' ? 'text-gray-400' : 'text-gray-800'}`}>
                                        {textOf(lesson)}
                                          </div>
                                      {lesson.duration && (
                                            <div className="flex items-center text-sm text-gray-500 mt-1">
                                              <Clock className="h-4 w-4 mr-1" />
                                              <span className="font-medium">{lesson.duration}</span>
                                              <span className="ml-3 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">
                                                {lesson.type}
                                              </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center">
                                        <span className="text-purple-400 mr-2">→</span>
                                        <ChevronRight className="h-5 w-5 text-purple-400" />
                                  </div>
                                </button>
                              ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }) : (
                      <div className="text-center py-12">
                        <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-3xl p-8 mx-4">
                          <div className="text-6xl mb-4">🚀</div>
                          <h3 className="text-2xl font-bold text-gray-800 mb-3">
                            Amazing content coming soon!
                          </h3>
                          <p className="text-gray-600 text-lg leading-relaxed">
                            We're crafting something special just for you. Your learning adventure is being prepared with care and excitement!
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  {language === 'en' ? 'Student Reviews' : 'ကျောင်းသားများ၏ သုံးသပ်ချက်များ'}
                </h2>
                <div className="text-center py-10">
                  <HelpCircle className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">
                    {language === 'en'
                      ? 'Reviews coming soon'
                      : 'သုံးသပ်ချက်များ မကြာမီ ရောက်ရှိလာမည်'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Hidden on Mobile, Shown on Desktop */}
          <div className="hidden md:block space-y-6">
            {/* Quick Stats */}
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-800 mb-4">
                {language === 'en' ? 'Course Features' : 'သင်တန်းအင်္ဂါရပ်များ'}
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{language === 'en' ? 'Level' : 'အဆင့်'}</span>
                  <span className="font-medium text-gray-800">{path.level}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{language === 'en' ? 'Duration' : 'ကြာချိန်'}</span>
                  <span className="font-medium text-gray-800">{path.duration}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-600">{language === 'en' ? 'Modules' : 'အပိုင်းများ'}</span>
                  <span className="font-medium text-gray-800">{sortedModules.length}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{language === 'en' ? 'Language' : 'ဘာသာစကား'}</span>
                  <span className="font-medium text-gray-800">
                    {language === 'en' ? 'English/Burmese' : 'အင်္ဂလိပ်/မြန်မာ'}
                  </span>
                </div>
              </div>
            </div>

            {/* Certificate Preview */}
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <div className="flex items-center mb-3">
                <Certificate className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="font-semibold text-gray-800">
                  {language === 'en' ? 'Certificate Included' : 'အသိအမှတ်ပြုလက်မှတ် ပါဝင်သည်'}
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                {language === 'en'
                  ? 'Earn a certificate of completion when you finish this course'
                  : 'ဤသင်တန်းပြီးဆုံးသောအခါ ပြီးမြောက်ကြောင်း အသိအမှတ်ပြုလက်မှတ် ရရှိမည်'}
              </p>
            </div>

            {/* Help Card */}
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-5">
              <div className="flex items-center mb-3">
                <HelpCircle className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="font-semibold text-blue-800">
                  {language === 'en' ? 'Need Help?' : 'အကူအညီလိုပါသလား။'}
                </h3>
              </div>
              <p className="text-sm text-blue-700 mb-4">
                {language === 'en'
                  ? 'Our support team is here to help you with any questions about the course.'
                  : 'သင်တန်းနှင့်ပတ်သက်သော မေးခွန်းများအတွက် ကျွန်ုပ်တို့၏ အကူအညီပေးရေးအဖွဲ့က ကူညီပေးရန် အသင့်ရှိပါသည်။ '}
              </p>
              <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center text-sm">
                <BookOpen className="h-4 w-4 mr-2" />
                {language === 'en' ? 'View Course Guide' : 'သင်တန်းလမ်းညွှန် ကြည့်ရှုရန်'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        onUpgrade={handlePremiumUpgrade}
        title={isCoursePremium() ? "Unlock Your Premium Journey" : "Unlock Premium Modules"}
        description={isCoursePremium() 
          ? "This entire journey is premium content with advanced modules, personalized guidance, and exclusive resources designed to accelerate your success."
          : "Get access to advanced modules with personalized guidance and exclusive content."
        }
      />
    </div>
  );
};

export default CourseDetail;